import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import { ExternalLink } from 'lucide-react';
import { PropertyImage } from '@/features/property/components/PropertyImage';
import { SimilarityScore } from '@/features/metrics/components/SimilarityScore';
import { DistanceDisplay } from '@/features/metrics/components/DistanceDisplay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/designSystem/Dialog";
import { Button } from '@/designSystem/Button';
import { PropertyType, BaseProperty } from '@/features/property/types';
import { Competitor } from '@/features/compset/types';
import { getCurrencySymbolFromCountryCode } from '@/utils';
import MatchingTable from '@/features/compset/components/MatchingTable';
import { SimilarityBreakdown } from '@/features/compset/components/SimilarityBreakdown';
import { PROPERTY_COLORS } from '@/features/property/utils/consts';

// Marker icons - unchanged from original
const marketPropertyIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${PROPERTY_COLORS.market.fill}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 6-8 12-8 12s-8-6-8-10a8 8 0 0 1 16 0z"></path><circle cx="12" cy="10" r="3" fill="white"></circle></svg>`)}`,
  iconSize: [20, 20],
  iconAnchor: [10, 20],
  popupAnchor: [0, -20],
});

const competitorIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${PROPERTY_COLORS.competitor.fill}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 6-8 12-8 12s-8-6-8-10a8 8 0 0 1 16 0z"></path><circle cx="12" cy="10" r="3" fill="white"></circle></svg>`)}`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
});

const ourPropertyIcon = new L.Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${PROPERTY_COLORS.ownProperty.fill}" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-map-pin"><path d="M20 10c0 6-8 12-8 12s-8-6-8-10a8 8 0 0 1 16 0z"></path><circle cx="12" cy="10" r="3" fill="white"></circle></svg>`)}`,
  iconSize: [35, 35],
  iconAnchor: [17.5, 35],
  popupAnchor: [0, -35],
});

const componentMap: Record<PropertyType, { icon: typeof marketPropertyIcon }> = {
  'competitor': {
    icon: competitorIcon
  },
  'ownProperty': {
    icon: ourPropertyIcon
  },
  'market': {
    icon: marketPropertyIcon
  }
};

interface PropertyMarkerProps {
  property: BaseProperty | Competitor;
  onPropertyClick: (property: BaseProperty | Competitor) => void;
  type: PropertyType
  isSelected: boolean;
}

export const PropertyMarker: React.FC<PropertyMarkerProps> = ({
  property,
  onPropertyClick,
  isSelected,
  type,
}) => {
  const markerRef = useRef<L.Marker>(null);
  const isOwnProperty = type === 'ownProperty';
  const isCompetitor = type === 'competitor';

  useEffect(() => {
    if (markerRef.current) {
      const markerElement = markerRef.current.getElement();
      if (markerElement) {
        if (isSelected) {
          markerElement.classList.add('selected-marker');
        } else {
          markerElement.classList.remove('selected-marker');
        }
      }
    }
  }, [isSelected]);

  return (
    <Marker
      position={[property.latitude, property.longitude]}
      icon={componentMap[type].icon}
      eventHandlers={{
        click: () => {
          markerRef.current?.openPopup();
          onPropertyClick(property);
        }
      }}
      ref={markerRef}
    >
      <Popup className="property-popup">
        <div className="text-sm w-full">
          <div className="flex flex-col">
            <div className="mb-3 rounded-md w-32 h-32">
              <PropertyImage photoPath={property.photo_path} />
            </div>

            <div className="font-bold flex items-center gap-2 mb-2">
              {isOwnProperty && (
                <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">
                  Your Property
                </span>
              )}
              <span>{property.name}</span>
            </div>

            {isCompetitor && (
              <SimilarityScore
                score={(property as Competitor).competitor_score}
                className="mb-2"
                compact={true}
              />
            )}

            {/* Similarity breakdown section for competitors */}
            {isCompetitor && (property as Competitor).matching_details && (
              <div className="border-t border-gray-100 p-2 mb-2">
                <SimilarityBreakdown
                  matchingDetails={(property as Competitor).matching_details}
                />
              </div>
            )}

            <div className="flex justify-between items-center mb-3">
              {isCompetitor && (
                <DistanceDisplay
                  distanceKm={(property as Competitor).distance_km}
                  textSize="text-sm"
                />
              )}
              <a
                href={`https://www.booking.com/${property.path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded-full bg-gray-100 text-gray-500 hover:bg-blue-200 hover:text-blue-700 transition-colors"
                title="View on Booking.com"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink size={16} />
              </a>
            </div>

            {/* Modal trigger for detailed comparison */}
            {isCompetitor && (property as Competitor).matching_details && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full flex items-center justify-center py-1.5 text-xs rounded"
                  >
                    View Detailed Comparison
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto z-[10000]">
                  <DialogHeader>
                    <DialogTitle>Competitor Similarity Analysis: {property.name}</DialogTitle>
                  </DialogHeader>
                  <MatchingTable
                    matchingDetails={(property as Competitor).matching_details}
                    currencySymbol={getCurrencySymbolFromCountryCode(property.country_code)}
                    distanceKM={(property as Competitor).distance_km}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

PropertyMarker.displayName = 'PropertyMarker';